import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useApi, useAuth } from "./AuthContext";
import InvoicePreview from "./InvoicePreview";
import BackButton from "./BackButton";
import CurrencyInput from "./CurrencyInput";
import MobileFinancialDashboard from "./MobileFinancialDashboard";
import useIsMobile from "../utils/useIsMobile";
import { formatUSD } from "../utils/currency";
import useAdminEvents from "./useAdminEvents";
import {
  DollarSign, TrendingUp, AlertTriangle, CheckCircle, FileText, Send,
  Trash2, Eye, Plus, ArrowLeft, Download, Check, X, Phone, Mail,
  Wallet, ArrowDownCircle, ArrowUpCircle, Receipt, Upload, FolderOpen,
  Filter, CalendarDays, BarChart3, Package, CreditCard, Store, Search,
  User, RefreshCw, Info, Clock, CalendarClock, Repeat, Sparkles
} from "lucide-react";
import { toast } from "sonner";

const REIMB_COLORS = {
  none: "",
  awaiting: "bg-orange-50 text-orange-700",
  reimbursed: "bg-cyan-50 text-cyan-700",
};

const REIMB_LABELS = { none: "N/A", awaiting: "Awaiting", reimbursed: "Reimbursed" };

const INV_STATUS_COLORS = {
  draft: "bg-stone-100 text-stone-700",
  sent: "bg-amber-50 text-amber-700",
  paid: "bg-green-50 text-green-700",
  overdue: "bg-red-50 text-red-700",
};

const EXP_STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700",
  scheduled: "bg-blue-50 text-blue-700",
  paid: "bg-green-50 text-green-700",
};

const EXPENSE_CATS = [
  { value: "fuel", label: "Fuel" },
  { value: "mulch", label: "Mulch / Materials" },
  { value: "equipment", label: "Equipment" },
  { value: "supplies", label: "Supplies" },
  { value: "labor", label: "Labor" },
  { value: "insurance", label: "Insurance" },
  { value: "vehicle", label: "Vehicle" },
  { value: "other", label: "Other" },
];

const GOW_INFO = {
  name: "Georgia Outdoor Worx LLC",
  taxId: "",
  paymentType: "Zelle / Check / Transfer",
  bank: "Wells Fargo",
  routing: "",
  account: "",
  currency: "USD",
  phone: "478.418.2629",
  email: "contactus@georgiaoutdoorworx.com",
  website: "www.georgiaoutdoorworx.com",
};

// ====================== FOLDER TAB ======================
function FolderTab({ id, label, icon: Icon, active, onClick, badge }) {
  return (
    <button
      data-testid={`tab-${id}`}
      onClick={onClick}
      className={`relative flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-t-xl border-x border-t transition-all ${
        active
          ? "bg-white border-stone-200 text-forest-700 -mb-px z-10"
          : "bg-stone-100 border-stone-200/50 text-stone-500 hover:text-stone-700 hover:bg-stone-50"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {badge > 0 && <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white">{badge}</span>}
    </button>
  );
}

// ====================== PROJECTED REVENUE PANEL ======================
const PRESETS = [
  { key: 3, label: "3 mo" },
  { key: 6, label: "6 mo" },
  { key: 12, label: "12 mo" },
  { key: 24, label: "24 mo" },
];

function ProjectedRevenuePanel({ data, loading, onRefresh, onMonthClick, range, onRangeChange }) {
  const months = data?.months || [];
  const grandTotal = data?.grand_total || 0;
  const oneTime = data?.one_time_total || 0;
  const recurring = data?.recurring_total || 0;
  const avg = data?.avg_per_month || 0;
  const tickets = data?.ticket_count || 0;

  // Find peak month for visual hierarchy
  const maxTotal = months.reduce((m, b) => Math.max(m, b.total || 0), 0);

  // Local-only state for the date inputs so a half-typed date doesn't
  // trigger a fetch every keystroke.
  const [draftStart, setDraftStart] = useState(range?.start || "");
  const [draftEnd, setDraftEnd] = useState(range?.end || "");
  useEffect(() => { setDraftStart(range?.start || ""); setDraftEnd(range?.end || ""); }, [range?.start, range?.end]);

  const applyCustom = () => {
    if (!draftStart || !draftEnd) { toast.error("Pick both start and end dates"); return; }
    if (draftEnd < draftStart) { toast.error("End date must be on or after start date"); return; }
    onRangeChange?.({ mode: "custom", months: 0, start: draftStart, end: draftEnd });
  };
  const applyPreset = (months) => onRangeChange?.({ mode: "preset", months, start: "", end: "" });

  // Heading subtitle reflects the active window so the user knows what's shown.
  const subtitle = (() => {
    if (range?.mode === "custom" && range.start && range.end) {
      const fmt = (s) => { try { const [y,m,d]=s.split("-"); return new Date(Number(y),Number(m)-1,Number(d)).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}); } catch { return s; } };
      return `${fmt(range.start)} → ${fmt(range.end)}`;
    }
    return `Next ${range?.months || 12} Months`;
  })();
  const cardLabel = range?.mode === "custom" && range.start && range.end ? "Grand Total · range" : `Grand Total · ${range?.months || 12} mo`;
  const monthsCovered = data?.months?.length || 0;
  const activeMonths = data?.non_zero_months || 0;

  return (
    <div className="space-y-6" data-testid="projected-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-forest-600" /> Future Revenue · {subtitle}
          </h2>
          <p className="text-xs text-stone-500 mt-1">
            Projected income from scheduled tickets and active recurring services. Updates in real-time as tickets change.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="projected-refresh"
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Date range filter — presets on the left, custom on the right */}
      <div
        data-testid="projected-range-bar"
        className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-medium uppercase tracking-wider text-stone-500 mr-1">Window</span>
          {PRESETS.map(p => {
            const active = range?.mode === "preset" && (range.months || 12) === p.key;
            return (
              <button
                key={p.key}
                type="button"
                data-testid={`projected-preset-${p.key}`}
                onClick={() => applyPreset(p.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                  active ? "bg-forest-600 border-forest-600 text-white shadow-sm" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-100"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-medium uppercase tracking-wider text-stone-500 mr-1">From / To</span>
          <input
            type="date"
            value={draftStart}
            onChange={(e) => setDraftStart(e.target.value)}
            data-testid="projected-start-date"
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-forest-500 bg-white"
          />
          <span className="text-stone-400 text-xs">→</span>
          <input
            type="date"
            value={draftEnd}
            onChange={(e) => setDraftEnd(e.target.value)}
            data-testid="projected-end-date"
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-xs focus:ring-2 focus:ring-forest-500 bg-white"
          />
          <button
            type="button"
            data-testid="projected-apply-range"
            onClick={applyCustom}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
              range?.mode === "custom" ? "bg-amber-500 border-amber-500 text-white shadow-sm" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-100"
            }`}
          >
            Apply
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-forest-600 to-forest-700 text-white rounded-xl p-5 shadow-sm" data-testid="projected-grand-total">
          <p className="text-[11px] font-medium uppercase tracking-wider text-forest-100 mb-1">{cardLabel}</p>
          <p className="text-3xl font-bold">{formatUSD(grandTotal)}</p>
          <p className="text-xs text-forest-100 mt-1">{tickets} ticket{tickets === 1 ? "" : "s"}</p>
        </div>
        <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
          <p className="text-[11px] font-medium uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> One-Time
          </p>
          <p className="text-2xl font-bold text-stone-800">{formatUSD(oneTime)}</p>
          <p className="text-xs text-stone-400 mt-1">{grandTotal > 0 ? Math.round((oneTime / grandTotal) * 100) : 0}% of total</p>
        </div>
        <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
          <p className="text-[11px] font-medium uppercase tracking-wider text-stone-500 mb-1 flex items-center gap-1">
            <Repeat className="w-3 h-3" /> Recurring
          </p>
          <p className="text-2xl font-bold text-stone-800">{formatUSD(recurring)}</p>
          <p className="text-xs text-stone-400 mt-1">{grandTotal > 0 ? Math.round((recurring / grandTotal) * 100) : 0}% of total</p>
        </div>
        <div className="bg-stone-50 rounded-xl p-5 border border-stone-200">
          <p className="text-[11px] font-medium uppercase tracking-wider text-stone-500 mb-1">Avg / Month</p>
          <p className="text-2xl font-bold text-stone-800">{formatUSD(avg)}</p>
          <p className="text-xs text-stone-400 mt-1">{activeMonths} of {monthsCovered} month{monthsCovered === 1 ? "" : "s"} active</p>
        </div>
      </div>

      {/* Month cards grid */}
      {loading && !months.length ? (
        <div className="text-center py-12 text-stone-400" data-testid="projected-loading">
          <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
          Loading projection...
        </div>
      ) : months.length === 0 ? (
        <div className="text-center py-12 text-stone-400">No projection data.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="projected-months-grid">
          {months.map((m, idx) => {
            const isPeak = m.total > 0 && m.total === maxTotal;
            const isEmpty = m.total === 0;
            const intensity = maxTotal > 0 ? Math.round((m.total / maxTotal) * 100) : 0;
            return (
              <button
                type="button"
                key={m.key}
                data-testid={`projected-month-${m.key}`}
                onClick={() => onMonthClick?.(m)}
                disabled={isEmpty}
                className={`group relative text-left rounded-xl p-4 border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-500 ${
                  isEmpty
                    ? "bg-stone-50/40 border-stone-200/60 cursor-not-allowed"
                    : isPeak
                      ? "bg-gradient-to-br from-forest-50 to-emerald-50 border-forest-300 shadow-sm hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
                      : "bg-white border-stone-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
                }`}
              >
                {isPeak && (
                  <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-forest-600 text-white shadow-sm">
                    Peak
                  </span>
                )}
                <div className="flex items-baseline justify-between mb-2">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isEmpty ? "text-stone-400" : "text-stone-500"}`}>
                    {m.label}
                  </p>
                  {idx === 0 && (
                    <span className="text-[10px] font-medium text-forest-600 bg-forest-50 px-1.5 py-0.5 rounded">This month</span>
                  )}
                </div>
                <p className={`text-2xl font-bold mb-2 ${isEmpty ? "text-stone-300" : "text-stone-800"}`}>
                  {formatUSD(m.total)}
                </p>
                {/* Intensity bar */}
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full transition-all ${isPeak ? "bg-forest-600" : "bg-forest-400"}`}
                    style={{ width: `${intensity}%` }}
                  />
                </div>
                {/* Breakdown */}
                <div className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1 text-stone-500" title="One-time tickets">
                    <Sparkles className="w-3 h-3 text-amber-500" />
                    {formatUSD(m.one_time_total || 0)}
                  </span>
                  <span className="flex items-center gap-1 text-stone-500" title="Recurring tickets">
                    <Repeat className="w-3 h-3 text-cyan-600" />
                    {formatUSD(m.recurring_total || 0)}
                  </span>
                </div>
                <div className="mt-2 pt-2 border-t border-stone-100 text-[11px] text-stone-400 flex items-center justify-between">
                  <span>{m.ticket_count} ticket{m.ticket_count === 1 ? "" : "s"}</span>
                  <span className="text-stone-300">{m.one_time_count}·OT / {m.recurring_count}·R</span>
                </div>
                {!isEmpty && (
                  <span className="absolute bottom-2 right-3 text-[10px] font-medium text-forest-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    View details →
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="text-[11px] text-stone-400 flex items-center gap-1.5 pt-2">
        <Info className="w-3 h-3" />
        <span>
          Excludes tickets already completed, cancelled, or converted to invoices (shown under Accounts Receivable).
          Period: <strong>{data?.period_start || "—"}</strong> → <strong>{data?.period_end || "—"}</strong>.
        </span>
      </div>
    </div>
  );
}

// ====================== MONTH DETAIL MODAL ======================
function MonthDetailModal({ open, monthKey, year, month, label, onClose, api, refreshSignal, onOpenServiceOrder }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!open || !year || !month) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/financial/projected-revenue/month?year=${year}&month=${month}`);
      setData(data);
    } catch (err) {
      console.warn("Month detail failed:", err?.message);
    } finally {
      setLoading(false);
    }
  }, [api, open, year, month]);

  useEffect(() => { fetchDetail(); }, [fetchDetail, refreshSignal]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const items = data?.items || [];
  const total = data?.total ?? 0;

  return (
    <div
      data-testid="month-detail-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm animate-in fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-forest-50 to-white">
          <div>
            <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-forest-600" />
              Scheduled Services · {label || monthKey}
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {loading ? "Loading…" : `${items.length} ticket${items.length === 1 ? "" : "s"} · ${formatUSD(total)}`}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="month-detail-close"
            className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && !data ? (
            <div className="py-16 text-center text-stone-400">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
              Loading tickets…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-stone-400">
              No scheduled tickets for this month.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="month-detail-table">
              <thead className="bg-stone-50 sticky top-0 z-10">
                <tr className="border-b border-stone-200">
                  <th className="text-left px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Date</th>
                  <th className="text-left px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Client</th>
                  <th className="text-left px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Property</th>
                  <th className="text-left px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Service</th>
                  <th className="text-center px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Type</th>
                  <th className="text-right px-5 py-3 font-semibold text-stone-600 uppercase tracking-wider text-[11px]">Value</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const clickable = !!onOpenServiceOrder;
                  return (
                  <tr
                    key={it.id}
                    onClick={clickable ? () => onOpenServiceOrder(it.id) : undefined}
                    className={`border-b border-stone-100 transition-colors ${clickable ? "cursor-pointer hover:bg-forest-50/50" : "hover:bg-stone-50"}`}
                    data-testid={`detail-row-${it.id}`}
                    title={clickable ? "Open this ticket in Service Orders" : undefined}
                  >
                    <td className="px-5 py-3 text-stone-600 whitespace-nowrap text-xs font-medium">
                      {it.scheduled_date}
                    </td>
                    <td className="px-5 py-3 text-stone-800 font-medium">{it.client_name}</td>
                    <td className="px-5 py-3 text-stone-600 text-xs max-w-xs truncate" title={it.property_address}>
                      {it.property_address}
                    </td>
                    <td className="px-5 py-3 text-stone-700 text-xs max-w-xs truncate" title={it.service_type}>
                      {it.service_type}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {it.is_recurring ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200">
                          <Repeat className="w-3 h-3" />
                          {it.frequency_label || "Recurring"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <Sparkles className="w-3 h-3" />
                          One-time
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-stone-800 whitespace-nowrap">
                      {formatUSD(it.total_amount)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
          <span className="text-sm text-stone-500">
            Total for <strong className="text-stone-700">{label || monthKey}</strong>
          </span>
          <span className="text-2xl font-bold text-forest-700" data-testid="month-detail-total">
            {formatUSD(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ====================== MAIN MODULE ======================
export default function FinancialModule({ readOnly = false, prefillInvoice = null, viewInvoiceId = null, onPrefillConsumed, onOpenBudgets, onOpenServiceOrder = null, initialFolder = null, onInitialFolderConsumed }) {
  const api = useApi();
  const { user: authUser } = useAuth();
  const [summary, setSummary] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState("projected");
  const [invFilter, setInvFilter] = useState("");
  const [expFilter, setExpFilter] = useState("");
  const [view, setView] = useState("main");
  const [mobileView, setMobileView] = useState("dashboard"); // "dashboard" | "invoices"
  const isMobile = useIsMobile();
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [providerInfo, setProviderInfo] = useState({ ...GOW_INFO });
  const [showExpForm, setShowExpForm] = useState(false);
  // Quick "Mark as Paid" modal state
  const [quickPaidInv, setQuickPaidInv] = useState(null);
  const [quickPaidDate, setQuickPaidDate] = useState("");
  const [quickPaidNote, setQuickPaidNote] = useState("");
  const [quickPaidBusy, setQuickPaidBusy] = useState(false);
  const [expForm, setExpForm] = useState({ category: "fuel", description: "", amount: 0, due_date: "", notes: "", payment_method: "", vendor: "", linked_asset: "", doc_status: "unpaid", reimbursement_status: "none", reimbursement_to: "" });
  const [expSummary, setExpSummary] = useState({ pending_month: 0, paid_month: 0, top_category: "none", top_category_total: 0 });
  const [expCatFilter, setExpCatFilter] = useState("");
  const [expDateFrom, setExpDateFrom] = useState("");
  const [expDateTo, setExpDateTo] = useState("");
  const [expCreatedByFilter, setExpCreatedByFilter] = useState("");
  const [expReimbFilter, setExpReimbFilter] = useState("");
  const [receiptModal, setReceiptModal] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [receiptExpense, setReceiptExpense] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [expReceiptFile, setExpReceiptFile] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);

  // Field Submissions state
  const [fieldSubs, setFieldSubs] = useState([]);
  const [fieldStatusFilter, setFieldStatusFilter] = useState("pending");
  const [fieldPreviewUrl, setFieldPreviewUrl] = useState(null);
  const [rejectingSub, setRejectingSub] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Invoice form
  const [invForm, setInvForm] = useState({ client_id: "", invoice_number: "", date: "", due_date: "", notes: "", items: [{ description: "", quantity: 1, unit_price: 0 }], paid_in_advance: false, show_bank_details: false });
  const [clientSearch, setClientSearch] = useState("");

  // Projected revenue (Future AR)
  // The user can pick a window via "preset" (3/6/12/24 mo from today) or a
  // fully-custom [startDate, endDate] range. Both modes are persisted to a
  // single piece of state to keep the fetch debounce logic simple.
  const [projected, setProjected] = useState(null);
  const [projectedLoading, setProjectedLoading] = useState(false);
  const projectedDebounceRef = useRef(null);
  const [projectedRange, setProjectedRange] = useState({ mode: "preset", months: 12, start: "", end: "" });
  // Month drill-down modal state
  const [detailMonth, setDetailMonth] = useState(null); // {year, month, key, label} | null
  const [detailRefreshSignal, setDetailRefreshSignal] = useState(0);

  const fetchProjected = useCallback(async () => {
    setProjectedLoading(true);
    try {
      const params = projectedRange.mode === "custom" && projectedRange.start && projectedRange.end
        ? `start_date=${projectedRange.start}&end_date=${projectedRange.end}`
        : `months=${projectedRange.months || 12}`;
      const { data } = await api.get(`/admin/financial/projected-revenue?${params}`);
      setProjected(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
      else console.warn("Failed to load projected revenue:", err?.message);
    } finally {
      setProjectedLoading(false);
    }
  }, [api, projectedRange]);

  useEffect(() => { fetchProjected(); }, [fetchProjected]);

  // Home shortcut → open a specific folder tab (projected/cashflow) on mount.
  useEffect(() => {
    if (!initialFolder) return;
    setActiveFolder(initialFolder);
    onInitialFolderConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolder]);

  // Real-time invalidation: any SO mutation refreshes the projection AND the open drill-down.
  // Debounced to coalesce bulk operations (e.g., creating recurring SOs).
  // `invoice_updated` (esp. → paid) also affects projections + AR cards.
  useAdminEvents((ev) => {
    if (!ev || !["so_created", "so_updated", "so_deleted", "invoice_updated"].includes(ev.type)) return;
    if (projectedDebounceRef.current) clearTimeout(projectedDebounceRef.current);
    projectedDebounceRef.current = setTimeout(() => {
      fetchProjected();
      setDetailRefreshSignal((s) => s + 1);
      // invoice_updated also bumps the main invoice list so the Cash Flow & AR
      // cards reflect the new status without a page reload.
      if (ev.type === "invoice_updated") {
        fetchAll();
      }
    }, 350);
  });

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (expFilter) params.append("status", expFilter);
      if (expCatFilter) params.append("category", expCatFilter);
      if (expDateFrom) params.append("date_from", expDateFrom);
      if (expDateTo) params.append("date_to", expDateTo);
      if (expCreatedByFilter) params.append("created_by", expCreatedByFilter);
      if (expReimbFilter) params.append("reimbursement", expReimbFilter);
      const [sRes, iRes, eRes, esRes] = await Promise.all([
        api.get("/admin/invoices/summary"),
        api.get(`/admin/invoices?status=${invFilter}`),
        api.get(`/admin/expenses?${params.toString()}`),
        api.get("/admin/expenses/summary"),
      ]);
      setSummary(sRes.data);
      setInvoices(iRes.data);
      setExpenses(eRes.data);
      setExpSummary(esRes.data);
    } catch (err) { toast.error("Failed to load financial data"); }
    finally { setLoading(false); }
  }, [api, invFilter, expFilter, expCatFilter, expDateFrom, expDateTo, expCreatedByFilter, expReimbFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/admin/clients"); setClients(data); } catch (err) { console.warn("Failed to load clients:", err?.message); } })();
  }, [api]);

  // Field Submissions helpers
  const fetchFieldSubs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (fieldStatusFilter) params.append("status", fieldStatusFilter);
      const { data } = await api.get(`/admin/field-submissions?${params.toString()}`);
      setFieldSubs(data);
    } catch (err) { console.warn("Failed to load field submissions:", err?.message); }
  }, [api, fieldStatusFilter]);

  useEffect(() => {
    if (activeFolder === "field") fetchFieldSubs();
  }, [activeFolder, fetchFieldSubs]);

  // Load once on mount so the badge is accurate without visiting the tab
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/admin/field-submissions?status=pending");
      setFieldSubs(data);
    } catch (_e) { /* silent */ }
  })(); }, [api]);

  const openFieldReceipt = async (subId) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem("admin_token");
      const res = await fetch(`${backend}/api/admin/field-submissions/${subId}/receipt`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      setFieldPreviewUrl(URL.createObjectURL(blob));
    } catch (_err) { toast.error("Failed to load receipt"); }
  };

  const closeFieldPreview = () => {
    if (fieldPreviewUrl) URL.revokeObjectURL(fieldPreviewUrl);
    setFieldPreviewUrl(null);
  };

  const approveFieldSub = async (sub) => {
    try {
      await api.post(`/admin/field-submissions/${sub.id}/approve`);
      toast.success(`Approved ${formatUSD(sub.amount)} from ${sub.user_name}`);
      fetchFieldSubs();
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Approval failed");
    }
  };

  const submitReject = async () => {
    if (!rejectingSub) return;
    try {
      await api.post(`/admin/field-submissions/${rejectingSub.id}/reject`, { reason: rejectReason });
      toast.success("Submission rejected");
      setRejectingSub(null); setRejectReason("");
      fetchFieldSubs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reject failed");
    }
  };

  const selectedClient = useMemo(() => clients.find(c => c.id === invForm.client_id), [clients, invForm.client_id]);
  const clientData = useMemo(() => ({
    name: selectedClient?.name || editingInvoice?.client_name || "",
    company_name: selectedClient?.company_name || editingInvoice?.company_name || "",
    responsible_contact: selectedClient?.responsible_contact || editingInvoice?.responsible_contact || "",
    tax_id: selectedClient?.tax_id || editingInvoice?.tax_id || "",
    address: selectedClient?.address || editingInvoice?.client_address || "",
  }), [selectedClient, editingInvoice]);
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 10);
    const q = clientSearch.toLowerCase();
    return clients.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)).slice(0, 10);
  }, [clients, clientSearch]);
  const invTotal = useMemo(() => invForm.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0), [invForm.items]);

  const pendingAR = useMemo(() => invoices.filter(i => i.status === "sent" || i.status === "overdue").length, [invoices]);
  const pendingAP = useMemo(() => expenses.filter(e => e.status === "pending" || e.status === "scheduled").length, [expenses]);
  const fieldPending = useMemo(() => fieldSubs.filter(s => s.status === "pending").length, [fieldSubs]);

  // Invoice helpers
  const openInvEditor = (inv = null) => {
    if (inv) {
      setEditingInvoice(inv);
      setInvForm({ client_id: inv.client_id, invoice_number: inv.invoice_number || "", date: inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-US") : "", due_date: inv.due_date || "", notes: inv.notes || "", items: inv.items?.length ? inv.items : [{ description: "", quantity: 1, unit_price: 0 }], service_order_id: inv.service_order_id || "", paid_in_advance: !!inv.paid_in_advance || inv.status === "paid", show_bank_details: !!inv.show_bank_details });
    } else {
      const today = new Date().toISOString().split("T")[0]; const due = new Date(); due.setDate(due.getDate() + 30);
      setInvForm({ client_id: "", invoice_number: "", date: today, due_date: due.toISOString().split("T")[0], notes: "", items: [{ description: "", quantity: 1, unit_price: 0 }], service_order_id: "", paid_in_advance: false, show_bank_details: false });
      setEditingInvoice(null);
    }
    setClientSearch(""); setView("inv-editor");
  };

  // Consume invoice prefill from Service Orders module — opens editor with pre-populated fields.
  useEffect(() => {
    if (!prefillInvoice) return;
    const today = new Date().toISOString().split("T")[0];
    const due = new Date(); due.setDate(due.getDate() + 30);
    setEditingInvoice(null);
    setInvForm({
      client_id: prefillInvoice.client_id || "",
      invoice_number: "",
      date: today,
      due_date: due.toISOString().split("T")[0],
      notes: prefillInvoice.notes || "",
      items: prefillInvoice.items && prefillInvoice.items.length
        ? prefillInvoice.items.map(it => ({
            description: it.description || "",
            quantity: Number(it.quantity || 1),
            unit_price: Number(it.unit_price || 0),
          }))
        : [{ description: "", quantity: 1, unit_price: 0 }],
      service_order_id: prefillInvoice.service_order_id || "",
      paid_in_advance: false,
      show_bank_details: false,
    });
    setClientSearch(prefillInvoice.client_name || "");
    setView("inv-editor");
    if (onPrefillConsumed) onPrefillConsumed();
    toast.success("Invoice prefilled from service order — review and save");
  }, [prefillInvoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consume viewInvoiceId — load and open the linked invoice for review.
  useEffect(() => {
    if (!viewInvoiceId) return;
    (async () => {
      try {
        let inv = invoices.find(i => i.id === viewInvoiceId);
        if (!inv) {
          const { data } = await api.get(`/admin/invoices/${viewInvoiceId}`);
          inv = data;
        }
        if (inv) openInvEditor(inv);
      } catch (_e) {
        toast.error("Linked invoice not found");
      } finally {
        if (onPrefillConsumed) onPrefillConsumed();
      }
    })();
  }, [viewInvoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addInvItem = () => setInvForm(f => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeInvItem = (i) => setInvForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateInvItem = (i, field, val) => setInvForm(f => { const items = [...f.items]; items[i] = { ...items[i], [field]: field === "description" ? val : Number(val) }; return { ...f, items }; });

  const handleSaveInv = async () => {
    if (!invForm.client_id) { toast.error("Select a client"); return; }
    try {
      const items = invForm.items.filter(i => i.description).map(i => ({ ...i, total: (i.quantity || 0) * (i.unit_price || 0) }));
      if (editingInvoice) {
        const { data: updated } = await api.put(`/admin/invoices/${editingInvoice.id}`, {
          notes: invForm.notes,
          due_date: invForm.due_date,
          paid_in_advance: invForm.paid_in_advance,
          show_bank_details: invForm.show_bank_details,
          items,
          total_amount: invTotal,
        });
        setEditingInvoice(updated);
        toast.success("Invoice updated");
      } else {
        const payload = { client_id: invForm.client_id, items, total_amount: invTotal, due_date: invForm.due_date, notes: invForm.notes, paid_in_advance: invForm.paid_in_advance, show_bank_details: invForm.show_bank_details };
        if (invForm.service_order_id) payload.service_order_id = invForm.service_order_id;
        const { data: created } = await api.post("/admin/invoices", payload);
        setEditingInvoice(created);
        setInvForm(f => ({ ...f, invoice_number: created.invoice_number }));
        toast.success(invForm.paid_in_advance ? `Invoice ${created.invoice_number} created & marked as PAID` : `Invoice ${created.invoice_number} created`);
      }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || err?.message || "Failed to save invoice"); }
  };

  const handleInvStatus = async (inv, status) => {
    if (!window.confirm(`${status === "paid" ? "Mark as paid" : `Change to ${status}`}?`)) return;
    try { await api.put(`/admin/invoices/${inv.id}`, { status }); toast.success(`Status: ${status}`); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Open quick-paid modal (defaults payment date to today)
  const openQuickPaidModal = (inv) => {
    setQuickPaidInv(inv);
    setQuickPaidDate(new Date().toISOString().split("T")[0]);
    setQuickPaidNote("");
  };

  const closeQuickPaidModal = () => {
    if (quickPaidBusy) return;
    setQuickPaidInv(null);
    setQuickPaidDate("");
    setQuickPaidNote("");
  };

  const confirmQuickPaid = async () => {
    if (!quickPaidInv) return;
    setQuickPaidBusy(true);
    try {
      const existingNotes = quickPaidInv.notes || "";
      const noteLine = `Paid ${quickPaidDate}${quickPaidNote.trim() ? ` · ${quickPaidNote.trim()}` : ""}`;
      const mergedNotes = existingNotes ? `${existingNotes}\n${noteLine}` : noteLine;
      await api.put(`/admin/invoices/${quickPaidInv.id}`, {
        paid_in_advance: true,
        notes: mergedNotes,
      });
      toast.success(`${quickPaidInv.invoice_number} marked as paid`);
      closeQuickPaidModal();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setQuickPaidBusy(false);
    }
  };

  const handleDeleteInv = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    try { await api.delete(`/admin/invoices/${inv.id}`); toast.success("Deleted"); fetchAll(); }
    catch (err) { toast.error("Failed to delete"); }
  };

  const [sendingInvoice, setSendingInvoice] = useState(false);
  const handleSendInvoice = async (inv) => {
    if (!inv?.id) return;
    setSendingInvoice(true);
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/send`);
      // Reflect new state locally without a full refetch
      setEditingInvoice(prev => prev ? {
        ...prev,
        is_sent: true,
        last_sent_at: data?.last_sent_at,
        invoice_token: data?.public_link ? data.public_link.split("/").pop() : prev.invoice_token,
        token_expires_at: data?.token_expires_at,
      } : prev);
      toast.success(inv.is_sent ? "Invoice re-sent (link refreshed)" : "Invoice sent to client");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invoice");
    } finally { setSendingInvoice(false); }
  };

  const handleExportPDF = async () => {
    const el = document.getElementById("invoice-preview");
    if (!el) return; toast.info("Generating PDF...");
    try { const html2pdf = (await import("html2pdf.js")).default; html2pdf().set({ margin: 0, filename: `${invForm.invoice_number || editingInvoice?.invoice_number || "invoice"}.pdf`, image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "in", format: "letter", orientation: "portrait" } }).from(el).save(); }
    catch (err) { toast.error("PDF export failed"); }
  };

  // Expense helpers
  const EMPTY_EXP_FORM = { category: "fuel", description: "", amount: 0, due_date: "", notes: "", payment_method: "", vendor: "", linked_asset: "", doc_status: "unpaid", reimbursement_status: "none", reimbursement_to: "" };

  const handleSaveExp = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await api.put(`/admin/expenses/${editingExpense.id}`, expForm);
        if (expReceiptFile) {
          const fd = new FormData();
          fd.append("file", expReceiptFile);
          await api.post(`/admin/expenses/${editingExpense.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Expense updated");
      } else {
        const { data } = await api.post("/admin/expenses", expForm);
        if (expReceiptFile && data?.id) {
          const fd = new FormData();
          fd.append("file", expReceiptFile);
          await api.post(`/admin/expenses/${data.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Expense added");
      }
      setShowExpForm(false); setEditingExpense(null);
      setExpForm(EMPTY_EXP_FORM);
      setExpReceiptFile(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleEditExp = (exp) => {
    setEditingExpense(exp);
    setExpForm({
      category: exp.category || "fuel", description: exp.description || "",
      amount: exp.amount || 0, due_date: exp.due_date || "",
      notes: exp.notes || "", payment_method: exp.payment_method || "",
      vendor: exp.vendor || "", linked_asset: exp.linked_asset || "",
      doc_status: exp.doc_status || "unpaid",
      reimbursement_status: exp.reimbursement_status || "none",
      reimbursement_to: exp.reimbursement_to || "",
    });
    setExpReceiptFile(null);
    setShowExpForm(true);
  };

  const handleExpStatus = async (exp, status) => {
    setConfirmModal({
      title: `Change status to "${status}"?`,
      message: `This will update expense "${exp.description}" to ${status}.`,
      onConfirm: async () => {
        try { await api.put(`/admin/expenses/${exp.id}`, { status }); toast.success(`Expense: ${status}`); fetchAll(); }
        catch (err) { toast.error("Failed to update expense"); }
        setConfirmModal(null);
      }
    });
  };

  const handleExpReimb = async (exp, reimb) => {
    setConfirmModal({
      title: `Change reimbursement to "${REIMB_LABELS[reimb]}"?`,
      message: `This will update reimbursement status for "${exp.description}".`,
      onConfirm: async () => {
        try { await api.put(`/admin/expenses/${exp.id}`, { reimbursement_status: reimb }); toast.success(`Reimbursement: ${REIMB_LABELS[reimb]}`); fetchAll(); }
        catch (err) { toast.error("Failed to update"); }
        setConfirmModal(null);
      }
    });
  };

  const handleDeleteExp = async (exp) => {
    setConfirmModal({
      title: "Delete this expense?",
      message: `"${exp.description}" (${formatUSD(exp.amount || 0)}) will be permanently removed.`,
      danger: true,
      onConfirm: async () => {
        try { await api.delete(`/admin/expenses/${exp.id}`); toast.success("Deleted"); fetchAll(); }
        catch (err) { toast.error("Failed to delete expense"); }
        setConfirmModal(null);
      }
    });
  };

  const handleUploadReceipt = async (expenseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/admin/expenses/${expenseId}/receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt uploaded");
      fetchAll();
    } catch (err) { toast.error("Upload failed"); }
  };

  const handleViewReceipt = async (path, expense) => {
    try {
      const resp = await api.get(`/admin/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setReceiptUrl(url);
      setReceiptExpense(expense || null);
      setReceiptModal(true);
    } catch (err) { toast.error("Failed to load receipt"); }
  };

  const exportExpensesCSV = () => {
    const headers = ["Date", "Category", "Description", "Vendor", "Amount", "Payment Method", "Asset", "Status", "Doc Status", "Reimbursement", "Reimb. To", "Created By"];
    const rows = expenses.map(e => [e.due_date || e.created_at?.slice(0, 10), e.category, e.description, e.vendor || "", `${formatUSD(e.amount || 0)}`, e.payment_method || "", e.linked_asset || "", e.status, e.doc_status || "", e.reimbursement_status || "", e.reimbursement_to || "", e.created_by_name || ""]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `expenses_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  // ====================== INVOICE EDITOR VIEW ======================
  if (view === "inv-editor") {
    return (
      <div data-testid="invoice-editor" className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3 bg-white rounded-xl border border-stone-200 px-4 py-3 shadow-sm">
          <h2 className="font-semibold text-stone-800">{editingInvoice ? `Invoice ${editingInvoice.invoice_number}` : "New Invoice"}</h2>
          <div className="flex items-center gap-2">
            {!readOnly && editingInvoice && (
              <button
                onClick={() => handleSendInvoice(editingInvoice)}
                disabled={sendingInvoice}
                data-testid="send-invoice-btn"
                title={editingInvoice.is_sent ? "Re-send invoice (refreshes the 72h link)" : "Send invoice to client"}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  editingInvoice.is_sent
                    ? "border-forest-300 bg-forest-50 text-forest-700 hover:bg-forest-100"
                    : "border-stone-300 text-stone-700 hover:bg-stone-50"
                } disabled:opacity-50`}
              >
                {sendingInvoice ? <RefreshCw className="w-4 h-4 animate-spin" />
                  : editingInvoice.is_sent ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                {sendingInvoice ? "Sending…" : editingInvoice.is_sent ? "Invoice Sent" : "Send Invoice"}
              </button>
            )}
            <button onClick={handleExportPDF} data-testid="export-invoice-pdf" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50"><Download className="w-4 h-4" /> Export PDF</button>
            {!readOnly && !editingInvoice && <button onClick={handleSaveInv} data-testid="save-invoice-btn" className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium"><Check className="w-4 h-4" /> Create</button>}
            {!readOnly && editingInvoice && <button onClick={handleSaveInv} className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium"><Check className="w-4 h-4" /> Update</button>}
          </div>
        </div>
        <BackButton onClick={() => setView("main")} testId="invoice-editor-back" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* LEFT: Form */}
          <div className="space-y-4 max-h-[calc(100vh-180px)] overflow-y-auto pr-1">
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Client</h3>
              {editingInvoice ? (
                <div className="bg-stone-50 rounded-lg px-4 py-3"><p className="font-medium text-stone-800">{editingInvoice.client_name}</p></div>
              ) : (
                <div className="relative">
                  <input data-testid="invoice-client-search" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Search client..." className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                  {clientSearch && !invForm.client_id && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredClients.map(c => (<button key={c.id} onClick={() => { setInvForm(f => ({ ...f, client_id: c.id })); setClientSearch(c.name); }} className="w-full text-left px-3 py-2 hover:bg-stone-50 text-sm border-b border-stone-100"><span className="font-medium">{c.name}</span>{c.email && <span className="text-stone-400 ml-2 text-xs">{c.email}</span>}</button>))}
                    </div>
                  )}
                  {invForm.client_id && selectedClient && (<div className="mt-2 bg-forest-50 rounded-lg px-3 py-2 flex items-center justify-between"><span className="text-sm text-forest-700 font-medium">{selectedClient.name}</span><button onClick={() => { setInvForm(f => ({ ...f, client_id: "" })); setClientSearch(""); }} className="text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button></div>)}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Details</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-stone-500 mb-1 block">Due Date</label><input type="date" value={invForm.due_date} onChange={(e) => setInvForm(f => ({ ...f, due_date: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-stone-500 mb-1 block">Notes</label><input value={invForm.notes || ""} onChange={(e) => setInvForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Optional" /></div>
              </div>
              <label
                data-testid="paid-in-advance-toggle"
                className={`mt-1 flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  invForm.paid_in_advance
                    ? "bg-green-50 border-green-400"
                    : "bg-stone-50 border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!invForm.paid_in_advance}
                  onChange={(e) => setInvForm(f => ({ ...f, paid_in_advance: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-green-600"
                />
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${invForm.paid_in_advance ? "text-green-800" : "text-stone-700"}`}>
                    Mark as PAID (paid in advance or on-site)
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">
                    When enabled, a large &ldquo;PAID&rdquo; stamp is displayed on the invoice PDF and the status is set to <strong>Paid</strong> automatically.
                  </p>
                </div>
              </label>
              <label
                data-testid="show-bank-details-toggle"
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  invForm.show_bank_details
                    ? "bg-teal-50 border-teal-400"
                    : "bg-stone-50 border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!invForm.show_bank_details}
                  onChange={(e) => setInvForm(f => ({ ...f, show_bank_details: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 accent-teal-600"
                />
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${invForm.show_bank_details ? "text-teal-800" : "text-stone-700"}`}>
                    Show Bank Details on Invoice
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">
                    When enabled, Truist Bank account and routing are displayed alongside the EIN. Disable for invoices where payment is not by bank transfer.
                  </p>
                </div>
              </label>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <div className="flex items-center justify-between"><h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Services</h3><button onClick={addInvItem} data-testid="add-service-btn" className="text-forest-600 text-xs font-medium flex items-center gap-1 hover:text-forest-700"><Plus className="w-3 h-3" /> Add Service</button></div>
              {invForm.items.map((item, idx) => (
                <div key={`svc-${idx}`} className="flex gap-2 items-center">
                  <input value={item.description} onChange={(e) => updateInvItem(idx, "description", e.target.value)} placeholder="Service description" className="flex-1 px-2 py-1.5 border border-stone-300 rounded text-sm" />
                  <CurrencyInput value={item.unit_price} onChange={(v) => updateInvItem(idx, "unit_price", v)} className="w-28 px-2 py-1.5 border border-stone-300 rounded text-sm" data-testid={`inv-item-price-${idx}`} />
                  <input type="number" value={item.quantity} onChange={(e) => updateInvItem(idx, "quantity", e.target.value)} placeholder="Qty" className="w-16 px-2 py-1.5 border border-stone-300 rounded text-sm text-center" />
                  <span className="text-sm font-medium text-stone-600 w-24 text-right">{formatUSD((item.quantity || 0) * (item.unit_price || 0))}</span>
                  {invForm.items.length > 1 && <button onClick={() => removeInvItem(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>}
                </div>
              ))}
              <div className="text-right text-sm font-bold text-teal-700 pt-2 border-t border-stone-200">Total: {formatUSD(invTotal)}</div>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Provider (GOW)</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-stone-500 mb-1 block">Payment</label><input value={providerInfo.paymentType} onChange={(e) => setProviderInfo(p => ({ ...p, paymentType: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-stone-500 mb-1 block">Bank</label><input value={providerInfo.bank} onChange={(e) => setProviderInfo(p => ({ ...p, bank: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></div>
              </div>
            </div>
          </div>
          {/* RIGHT: Preview */}
          <div className="max-h-[calc(100vh-180px)] overflow-y-auto bg-stone-100 rounded-xl p-4 border border-stone-200" data-testid="invoice-preview-area">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider flex items-center gap-1 mb-3"><Eye className="w-3.5 h-3.5" /> Live Preview</span>
            <InvoicePreview invoice={invForm} clientData={clientData} providerInfo={providerInfo} />
          </div>
        </div>
        {/* Modification History Timeline (only for saved invoices) */}
        {editingInvoice && (
          <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm" data-testid="invoice-history">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-stone-500" />
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Modification History</h3>
              <span className="text-xs text-stone-400">
                ({(editingInvoice.history_entries || []).length} {(editingInvoice.history_entries || []).length === 1 ? "entry" : "entries"})
              </span>
            </div>
            {(editingInvoice.history_entries || []).length === 0 ? (
              <p className="text-sm text-stone-400 italic py-2">No modifications recorded yet.</p>
            ) : (
              <ol className="relative border-l-2 border-stone-200 ml-2 space-y-3">
                {[...(editingInvoice.history_entries || [])].reverse().map((h) => (
                  <li key={h.id} className="ml-4" data-testid={`invoice-history-entry-${h.id}`}>
                    <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-teal-500 border-2 border-white shadow"></span>
                    <p className="text-sm text-stone-800">{h.action}</p>
                    <p className="text-[11px] text-stone-400 mt-0.5">
                      {new Date(h.created_at).toLocaleString("en-US", { year: "numeric", month: "short", day: "2-digit", hour: "numeric", minute: "2-digit", hour12: true })} · <span className="font-medium text-stone-500">{h.created_by}</span>
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    );
  }

  // ====================== MAIN VIEW WITH FOLDER TABS ======================
  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  // Quick action dispatcher used by mobile invoice action sheet
  const handleMobileInvoiceAction = async (inv, action) => {
    if (!inv) return;
    switch (action) {
      case "view":
        openInvEditor(inv);
        break;
      case "pdf": {
        // Load into editor and trigger PDF after preview renders
        openInvEditor(inv);
        setTimeout(() => { handleExportPDF(); }, 600);
        break;
      }
      case "mark_paid":
        openQuickPaidModal(inv);
        break;
      case "mark_sent":
        try {
          await api.put(`/admin/invoices/${inv.id}`, { status: "sent" });
          toast.success(`${inv.invoice_number} marked as sent`);
          fetchAll();
        } catch (err) { toast.error("Failed to update status"); }
        break;
      case "email":
        // Hook into webmail compose if available
        if (typeof window !== "undefined") {
          toast.info("Open the invoice to email it (full editor)");
          openInvEditor(inv);
        }
        break;
      case "delete":
        handleDeleteInv(inv);
        break;
      default:
        break;
    }
  };

  // ============== MOBILE LAYOUT ==============
  if (isMobile) {
    return (
      <>
        <MobileFinancialDashboard
          summary={summary}
          invoices={invoices}
          expSummary={expSummary}
          view={mobileView}
          onChangeView={setMobileView}
          onOpenInvoices={() => setMobileView("invoices")}
          onOpenAP={() => setMobileView("invoices") /* AP would deserve its own view but invoices is the closest mapping */}
          onNewInvoice={() => openInvEditor(null)}
          onOpenBudgets={() => { if (onOpenBudgets) onOpenBudgets(); else toast.info("Switch to Budgets from the More menu"); }}
          onQuickInvoiceAction={handleMobileInvoiceAction}
          onViewInvoice={(id) => { const inv = invoices.find(i => i.id === id); if (inv) openInvEditor(inv); }}
        />

        {/* Reuse quick-paid modal from desktop (already implemented further below) */}
        {quickPaidInv && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="quick-paid-modal-mobile">
            <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
              <h3 className="text-base font-semibold text-stone-900 mb-1">Mark as Paid</h3>
              <p className="text-xs text-stone-500 mb-3">
                {quickPaidInv.invoice_number} · {quickPaidInv.client_name} · {formatUSD(quickPaidInv.total_amount || 0)}
              </p>
              <label className="block text-xs font-medium text-stone-700 mb-1">Payment Date</label>
              <input
                type="date"
                value={quickPaidDate}
                onChange={(e) => setQuickPaidDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm mb-3"
              />
              <label className="block text-xs font-medium text-stone-700 mb-1">Note (optional)</label>
              <input
                value={quickPaidNote}
                onChange={(e) => setQuickPaidNote(e.target.value)}
                placeholder="e.g. paid via check #1234"
                className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm mb-4"
              />
              <div className="flex gap-2">
                <button onClick={closeQuickPaidModal} disabled={quickPaidBusy} className="flex-1 px-4 py-2.5 text-sm font-semibold text-stone-700 bg-stone-100 rounded-lg disabled:opacity-50">
                  Cancel
                </button>
                <button onClick={confirmQuickPaid} disabled={quickPaidBusy} data-testid="confirm-quick-paid-mobile" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg disabled:opacity-50">
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div data-testid="financial-module" className="space-y-0">
      {/* Folder Tabs */}
      <div className="flex items-end gap-1 pl-2 overflow-x-auto" data-testid="folder-tabs">
        <FolderTab id="projected" label="Future Revenue" icon={CalendarClock} active={activeFolder === "projected"} onClick={() => setActiveFolder("projected")} />
        <FolderTab id="cashflow" label="Cash Flow" icon={Wallet} active={activeFolder === "cashflow"} onClick={() => setActiveFolder("cashflow")} />
        <FolderTab id="ar" label="Accounts Receivable" icon={ArrowDownCircle} active={activeFolder === "ar"} onClick={() => setActiveFolder("ar")} badge={pendingAR} />
        <FolderTab id="ap" label="Accounts Payable" icon={ArrowUpCircle} active={activeFolder === "ap"} onClick={() => setActiveFolder("ap")} badge={pendingAP} />
        <FolderTab id="invoices" label="Invoices" icon={FileText} active={activeFolder === "invoices"} onClick={() => setActiveFolder("invoices")} />
        <FolderTab id="field" label="Field Submissions" icon={Receipt} active={activeFolder === "field"} onClick={() => setActiveFolder("field")} badge={fieldPending} />
      </div>

      {/* Folder Body */}
      <div className="bg-white border border-stone-200 rounded-b-xl rounded-tr-xl p-6 min-h-[400px] shadow-sm">

        {/* ====== FUTURE REVENUE (Projected AR, 12-month) ====== */}
        {activeFolder === "projected" && (
          <ProjectedRevenuePanel
            data={projected}
            loading={projectedLoading}
            onRefresh={fetchProjected}
            onMonthClick={(m) => setDetailMonth({ year: m.year, month: m.month, key: m.key, label: m.label })}
            range={projectedRange}
            onRangeChange={setProjectedRange}
          />
        )}

        {/* ====== CASH FLOW ====== */}
        {activeFolder === "cashflow" && (
          <div className="space-y-6" data-testid="cashflow-panel">
            <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2"><Wallet className="w-5 h-5 text-forest-600" /> Cash Flow Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-forest-50 rounded-xl p-5 border border-forest-200"><p className="text-xs font-medium text-forest-600 uppercase tracking-wider mb-1">Net Cash Flow</p><p className="text-2xl font-bold text-forest-700">{formatUSD(summary.net_cash_flow || 0)}</p></div>
              <div className="bg-green-50 rounded-xl p-5 border border-green-200"><p className="text-xs font-medium text-green-600 uppercase tracking-wider mb-1">Income (Paid)</p><p className="text-2xl font-bold text-green-700">{formatUSD(summary.total_paid || 0)}</p></div>
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200"><p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Receivable</p><p className="text-2xl font-bold text-amber-700">{formatUSD(summary.total_receivable || 0)}</p></div>
              <div className="bg-red-50 rounded-xl p-5 border border-red-200"><p className="text-xs font-medium text-red-600 uppercase tracking-wider mb-1">Expenses (Paid)</p><p className="text-2xl font-bold text-red-700">{formatUSD(summary.total_expenses_paid || 0)}</p></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-stone-50 rounded-xl p-5 border border-stone-200"><p className="text-sm font-medium text-stone-600 mb-1">Overdue Invoices</p><p className="text-xl font-bold text-red-600">{formatUSD(summary.total_overdue || 0)} <span className="text-sm font-normal text-stone-400">({summary.overdue_count || 0})</span></p></div>
              <div className="bg-stone-50 rounded-xl p-5 border border-stone-200"><p className="text-sm font-medium text-stone-600 mb-1">Pending Expenses</p><p className="text-xl font-bold text-amber-600">{formatUSD(summary.total_expenses_pending || 0)}</p></div>
            </div>
          </div>
        )}

        {/* ====== ACCOUNTS RECEIVABLE ====== */}
        {activeFolder === "ar" && (
          <div className="space-y-4" data-testid="ar-panel">
            <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2"><ArrowDownCircle className="w-5 h-5 text-forest-600" /> Accounts Receivable</h2></div>
            <div className="flex gap-2 flex-wrap">
              {["", "draft", "sent", "paid", "overdue"].map(s => (
                <button key={s || "all"} onClick={() => setInvFilter(s)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${invFilter === s ? "bg-forest-600 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}</button>
              ))}
            </div>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="ar-table">
                <thead><tr className="border-b bg-stone-50"><th className="text-left px-4 py-3 font-medium text-stone-600">Client</th><th className="text-right px-4 py-3 font-medium text-stone-600">Amount</th><th className="text-left px-4 py-3 font-medium text-stone-600">Due</th><th className="text-center px-4 py-3 font-medium text-stone-600">Status</th><th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th></tr></thead>
                <tbody>
                  {invoices.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-400">No invoices</td></tr> : invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3"><span className="font-medium text-stone-800">{inv.client_name}</span><span className="text-xs text-stone-400 ml-2">{inv.invoice_number}</span></td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(inv.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-stone-600">{inv.due_date || "—"}</td>
                      <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${INV_STATUS_COLORS[inv.status]}`}>{inv.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!readOnly && inv.status !== "paid" && (
                            <button
                              onClick={() => openQuickPaidModal(inv)}
                              data-testid={`quick-paid-${inv.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                              title="Mark this invoice as paid"
                            >
                              <CheckCircle className="w-3.5 h-3.5" /> Mark as Paid
                            </button>
                          )}
                          <button onClick={() => openInvEditor(inv)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500" title="View / Edit"><Eye className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ====== ACCOUNTS PAYABLE — PREMIUM ====== */}
        {activeFolder === "ap" && (
          <div className="space-y-5" data-testid="ap-panel">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-forest-600" /> Expense Management
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={exportExpensesCSV} data-testid="export-csv-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
                {!readOnly && (
                  <button onClick={() => setShowExpForm(!showExpForm)} data-testid="add-expense-btn" className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    <Plus className="w-4 h-4" /> Add Expense
                  </button>
                )}
              </div>
            </div>

            {/* Mini Dashboards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ap-mini-dashboards">
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-amber-600 uppercase tracking-wider mb-1">Total Pending</p>
                  <p className="text-2xl font-bold text-amber-700" data-testid="ap-pending-total">{formatUSD(expSummary.pending_month || 0)}</p>
                  <p className="text-xs text-amber-500 mt-0.5">This month</p>
                </div>
              </div>
              <div className="bg-green-50 rounded-xl p-5 border border-green-200 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wider mb-1">Total Paid</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="ap-paid-total">{formatUSD(expSummary.paid_month || 0)}</p>
                  <p className="text-xs text-green-500 mt-0.5">This month</p>
                </div>
              </div>
              <div className="bg-teal-50 rounded-xl p-5 border border-teal-200 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-1">Top Category</p>
                  <p className="text-lg font-bold text-teal-700 capitalize" data-testid="ap-top-category">{expSummary.top_category || "—"}</p>
                  <p className="text-xs text-teal-500 mt-0.5">{formatUSD(expSummary.top_category_total || 0)} total</p>
                </div>
              </div>
            </div>

            {/* Expense Form */}
            {showExpForm && (
              <form onSubmit={handleSaveExp} className="bg-stone-50 rounded-xl border border-stone-200 p-5 space-y-4" data-testid="expense-form">
                <p className="text-sm font-semibold text-stone-700 uppercase tracking-wider flex items-center gap-2"><Receipt className="w-4 h-4 text-forest-600" /> {editingExpense ? "Edit Expense" : "New Expense"}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Category</label>
                    <select value={expForm.category} onChange={(e) => setExpForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                      {EXPENSE_CATS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Description *</label>
                    <input data-testid="exp-description" value={expForm.description} onChange={(e) => setExpForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Amount ($) *</label>
                    <CurrencyInput data-testid="exp-amount" value={expForm.amount} onChange={(v) => setExpForm(f => ({ ...f, amount: v }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Due Date</label>
                    <input data-testid="exp-due-date" type="date" value={expForm.due_date} onChange={(e) => setExpForm(f => ({ ...f, due_date: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><CreditCard className="w-3 h-3" /> Payment Method</label>
                    <select data-testid="exp-payment-method" value={expForm.payment_method} onChange={(e) => setExpForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                      <option value="">Select...</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="zelle">Zelle</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="debit_card">Debit Card</option>
                      <option value="transfer">Bank Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><Store className="w-3 h-3" /> Vendor</label>
                    <input data-testid="exp-vendor" value={expForm.vendor} onChange={(e) => setExpForm(f => ({ ...f, vendor: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. Home Depot" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Doc Status *</label>
                    <select data-testid="exp-doc-status" value={expForm.doc_status} onChange={(e) => setExpForm(f => ({ ...f, doc_status: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                      <option value="unpaid">Unpaid (A Pagar)</option>
                      <option value="paid">Paid (Pago)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reimbursement</label>
                    <select data-testid="exp-reimb-status" value={expForm.reimbursement_status} onChange={(e) => setExpForm(f => ({ ...f, reimbursement_status: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                      <option value="none">N/A</option>
                      <option value="awaiting">Awaiting Reimbursement</option>
                      <option value="reimbursed">Reimbursed</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><Package className="w-3 h-3" /> Linked Asset</label>
                    <input data-testid="exp-linked-asset" value={expForm.linked_asset} onChange={(e) => setExpForm(f => ({ ...f, linked_asset: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="e.g. Truck #2" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><User className="w-3 h-3" /> Reimbursement To</label>
                    <input data-testid="exp-reimb-to" value={expForm.reimbursement_to || ""} onChange={(e) => setExpForm(f => ({ ...f, reimbursement_to: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Employee name" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block">Notes</label>
                    <input data-testid="exp-notes" value={expForm.notes || ""} onChange={(e) => setExpForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="Optional" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><Upload className="w-3 h-3" /> Receipt</label>
                    <input data-testid="exp-receipt-file" type="file" accept="image/*,.pdf" onChange={(e) => setExpReceiptFile(e.target.files?.[0] || null)} className="w-full px-3 py-1.5 border border-stone-300 rounded-lg text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-forest-50 file:text-forest-700" />
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button type="submit" data-testid="save-expense-btn" className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    <Check className="w-4 h-4" /> {editingExpense ? "Update Expense" : "Save Expense"}
                  </button>
                  <button type="button" onClick={() => { setShowExpForm(false); setEditingExpense(null); setExpReceiptFile(null); setExpForm(EMPTY_EXP_FORM); }} className="px-4 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-100 transition-colors">Cancel</button>
                  <span className="text-xs text-stone-400 flex items-center gap-1"><User className="w-3 h-3" /> Logged as: {authUser?.name || "—"}</span>
                </div>
              </form>
            )}

            {/* Advanced Filters */}
            <div className="flex gap-3 flex-wrap items-end bg-stone-50/80 rounded-xl border border-stone-200/80 p-4" data-testid="ap-filters">
              <div className="flex gap-1.5 flex-wrap items-center">
                <span className="text-xs font-medium text-stone-500 mr-1 flex items-center gap-1"><Filter className="w-3 h-3" /> Status:</span>
                {["", "pending", "scheduled", "paid"].map(s => (
                  <button key={s || "all"} onClick={() => setExpFilter(s)} data-testid={`filter-status-${s || "all"}`} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${expFilter === s ? "bg-forest-600 text-white shadow-sm" : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"}`}>
                    {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
                  </button>
                ))}
              </div>
              <div className="h-6 w-px bg-stone-200 hidden md:block" />
              <div className="flex items-end gap-2">
                <div>
                  <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><CalendarDays className="w-3 h-3" /> From</label>
                  <input type="date" value={expDateFrom} onChange={(e) => setExpDateFrom(e.target.value)} data-testid="filter-date-from" className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-500 mb-1 block">To</label>
                  <input type="date" value={expDateTo} onChange={(e) => setExpDateTo(e.target.value)} data-testid="filter-date-to" className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white" />
                </div>
              </div>
              <div className="h-6 w-px bg-stone-200 hidden md:block" />
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Category</label>
                <select value={expCatFilter} onChange={(e) => setExpCatFilter(e.target.value)} data-testid="filter-category" className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white min-w-[120px]">
                  <option value="">All Categories</option>
                  {EXPENSE_CATS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Reimb.</label>
                <select value={expReimbFilter} onChange={(e) => setExpReimbFilter(e.target.value)} data-testid="filter-reimbursement" className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white min-w-[110px]">
                  <option value="">All</option>
                  <option value="awaiting">Awaiting</option>
                  <option value="reimbursed">Reimbursed</option>
                  <option value="none">N/A</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block flex items-center gap-1"><User className="w-3 h-3" /> Created By</label>
                <input value={expCreatedByFilter} onChange={(e) => setExpCreatedByFilter(e.target.value)} data-testid="filter-created-by" placeholder="Name..." className="px-2.5 py-1.5 border border-stone-200 rounded-lg text-xs bg-white w-[110px]" />
              </div>
              {(expFilter || expCatFilter || expDateFrom || expDateTo || expCreatedByFilter || expReimbFilter) && (
                <button onClick={() => { setExpFilter(""); setExpCatFilter(""); setExpDateFrom(""); setExpDateTo(""); setExpCreatedByFilter(""); setExpReimbFilter(""); }} className="px-2.5 py-1.5 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" data-testid="clear-filters-btn">
                  <X className="w-3.5 h-3.5 inline mr-0.5" />Clear
                </button>
              )}
            </div>

            {/* Table or Empty State */}
            {expenses.length === 0 ? (
              <div className="bg-white rounded-xl border border-stone-200 p-12 flex flex-col items-center justify-center text-center" data-testid="ap-empty-state">
                <div className="w-20 h-20 rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
                  <FolderOpen className="w-10 h-10 text-stone-300" />
                </div>
                <h3 className="text-lg font-semibold text-stone-500 mb-1">No expenses found</h3>
                <p className="text-sm text-stone-400 max-w-sm">
                  {(expFilter || expCatFilter || expDateFrom || expDateTo)
                    ? "Try adjusting your filters to see more results."
                    : "Start tracking your business expenses by clicking \"Add Expense\" above."}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ap-table">
                    <thead>
                      <tr className="border-b bg-stone-50">
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Category</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Description</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Vendor</th>
                        <th className="text-right px-3 py-3 font-medium text-stone-600">Amount</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Payment</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Due</th>
                        <th className="text-center px-3 py-3 font-medium text-stone-600">Reimb.</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Reimb. To</th>
                        <th className="text-center px-3 py-3 font-medium text-stone-600">Receipt</th>
                        <th className="text-center px-3 py-3 font-medium text-stone-600">Status</th>
                        <th className="text-left px-3 py-3 font-medium text-stone-600">Created By</th>
                        <th className="text-right px-3 py-3 font-medium text-stone-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map(exp => (
                        <tr key={exp.id} className="border-b border-stone-100 hover:bg-stone-50/70 transition-colors" data-testid={`expense-row-${exp.id}`}>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-stone-100 text-stone-600 capitalize">{exp.category}</span>
                          </td>
                          <td className="px-3 py-3 text-stone-800 max-w-[180px] truncate">{exp.description}</td>
                          <td className="px-3 py-3 text-stone-600 text-xs">
                            {exp.vendor ? <span className="flex items-center gap-1"><Store className="w-3 h-3 text-stone-400" />{exp.vendor}</span> : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-right font-semibold text-stone-800">{formatUSD(exp.amount || 0)}</td>
                          <td className="px-3 py-3 text-xs text-stone-600">
                            {exp.payment_method ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium capitalize">
                                <CreditCard className="w-3 h-3" />{exp.payment_method.replace("_", " ")}
                              </span>
                            ) : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-stone-600 text-xs whitespace-nowrap">{exp.due_date || "—"}</td>
                          <td className="px-3 py-3 text-center">
                            {exp.reimbursement_status && exp.reimbursement_status !== "none" ? (
                              <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${REIMB_COLORS[exp.reimbursement_status]}`}>
                                {REIMB_LABELS[exp.reimbursement_status]}
                              </span>
                            ) : <span className="text-stone-300 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-3 text-xs text-stone-600">
                            {exp.reimbursement_to ? (
                              <span className="flex items-center gap-1 font-medium text-orange-700"><User className="w-3 h-3" />{exp.reimbursement_to}</span>
                            ) : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {exp.receipt_path ? (
                              <button onClick={() => handleViewReceipt(exp.receipt_path, exp)} data-testid={`view-receipt-${exp.id}`} title="View receipt" className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-forest-50 text-forest-700 hover:bg-forest-100 transition-colors">
                                <Receipt className="w-3.5 h-3.5" /> View
                              </button>
                            ) : !readOnly ? (
                              <label title="Upload receipt" className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-stone-100 text-stone-500 hover:bg-stone-200 cursor-pointer transition-colors" data-testid={`upload-receipt-${exp.id}`}>
                                <Upload className="w-3.5 h-3.5" /> Upload
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUploadReceipt(exp.id, e.target.files[0]); }} />
                              </label>
                            ) : <span className="text-xs text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${EXP_STATUS_COLORS[exp.status]}`}>{exp.status}</span>
                          </td>
                          <td className="px-3 py-3 text-xs text-stone-500">
                            {exp.created_by_name ? (
                              <span className="flex items-center gap-1"><User className="w-3 h-3 text-stone-400" />{exp.created_by_name}</span>
                            ) : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {!readOnly && (
                                <button onClick={() => handleEditExp(exp)} data-testid={`edit-exp-${exp.id}`} title="Edit expense" className="p-1 rounded-md hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors">
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {!readOnly && exp.status === "pending" && (
                                <button onClick={() => handleExpStatus(exp, "scheduled")} data-testid={`schedule-exp-${exp.id}`} title="Schedule payment" className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors">Schedule</button>
                              )}
                              {!readOnly && exp.status !== "paid" && (
                                <button onClick={() => handleExpStatus(exp, "paid")} data-testid={`pay-exp-${exp.id}`} title="Mark as paid" className="px-2 py-1 text-xs rounded bg-green-50 text-green-700 hover:bg-green-100 font-medium transition-colors">Paid</button>
                              )}
                              {!readOnly && exp.reimbursement_status === "awaiting" && (
                                <button onClick={() => handleExpReimb(exp, "reimbursed")} data-testid={`reimb-exp-${exp.id}`} title="Mark as reimbursed" className="px-2 py-1 text-xs rounded bg-cyan-50 text-cyan-700 hover:bg-cyan-100 font-medium transition-colors">Reimb.</button>
                              )}
                              {!readOnly && (
                                <button onClick={() => handleDeleteExp(exp)} data-testid={`delete-exp-${exp.id}`} title="Delete expense" className="p-1 rounded-md hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2.5 bg-stone-50 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500">
                  <span>{expenses.length} expense{expenses.length !== 1 ? "s" : ""}</span>
                  <span className="font-medium">Total: {formatUSD(expenses.reduce((s, e) => s + (e.amount || 0), 0))}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====== INVOICES ====== */}
        {activeFolder === "invoices" && (
          <div className="space-y-4" data-testid="invoices-panel">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2"><FileText className="w-5 h-5 text-forest-600" /> Invoices</h2>
              {!readOnly && <button onClick={() => openInvEditor()} data-testid="new-invoice-btn" className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium"><Plus className="w-4 h-4" /> New Invoice</button>}
            </div>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead><tr className="border-b bg-stone-50"><th className="text-left px-4 py-3 font-medium text-stone-600">Invoice #</th><th className="text-left px-4 py-3 font-medium text-stone-600">Client</th><th className="text-right px-4 py-3 font-medium text-stone-600">Amount</th><th className="text-left px-4 py-3 font-medium text-stone-600">Due</th><th className="text-center px-4 py-3 font-medium text-stone-600">Status</th><th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th></tr></thead>
                <tbody>
                  {invoices.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No invoices</td></tr> : invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 font-mono text-xs text-forest-600 font-medium">{inv.invoice_number}</td>
                      <td className="px-4 py-3 font-medium text-stone-800">{inv.client_name}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(inv.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-stone-600">{inv.due_date || "—"}</td>
                      <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${INV_STATUS_COLORS[inv.status]}`}>{inv.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openInvEditor(inv)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500"><Eye className="w-4 h-4" /></button>
                          {!readOnly && inv.status === "draft" && <button onClick={() => handleInvStatus(inv, "sent")} className="p-1.5 rounded-md hover:bg-amber-50 text-stone-500 hover:text-amber-600"><Send className="w-4 h-4" /></button>}
                          {!readOnly && inv.status !== "paid" && <button onClick={() => openQuickPaidModal(inv)} data-testid={`quick-paid-tab-${inv.id}`} className="p-1.5 rounded-md hover:bg-green-50 text-stone-500 hover:text-green-600" title="Mark as Paid"><CheckCircle className="w-4 h-4" /></button>}
                          {!readOnly && <button onClick={() => handleDeleteInv(inv)} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* ====== FIELD SUBMISSIONS ====== */}
        {activeFolder === "field" && (
          <div className="space-y-4" data-testid="field-submissions-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-forest-600" /> Field Submissions
              </h2>
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: "pending",  label: "Pending" },
                  { v: "approved", label: "Approved" },
                  { v: "rejected", label: "Rejected" },
                  { v: "",         label: "All" },
                ].map(opt => (
                  <button
                    key={opt.v || "all"}
                    data-testid={`field-filter-${opt.v || "all"}`}
                    onClick={() => setFieldStatusFilter(opt.v)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      fieldStatusFilter === opt.v
                        ? "bg-forest-600 text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <table className="w-full text-sm" data-testid="field-submissions-table">
                <thead>
                  <tr className="border-b bg-stone-50 text-stone-600">
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Submitted By</th>
                    <th className="text-left px-4 py-3 font-medium">Category</th>
                    <th className="text-right px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">SO / Notes</th>
                    <th className="text-center px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldSubs.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-400">No submissions</td></tr>
                  ) : fieldSubs.map(sub => (
                    <tr key={sub.id} data-testid={`field-row-${sub.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="px-4 py-3 text-stone-600 whitespace-nowrap">
                        {sub.created_at ? new Date(sub.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                      </td>
                      <td className="px-4 py-3 font-medium text-stone-800">{sub.user_name}</td>
                      <td className="px-4 py-3 capitalize text-stone-700">{sub.category}</td>
                      <td className="px-4 py-3 text-right font-semibold">{formatUSD(sub.amount || 0)}</td>
                      <td className="px-4 py-3 text-stone-600 hidden lg:table-cell max-w-xs">
                        {sub.service_order_ref && <div className="text-xs text-forest-700">SO: {sub.service_order_ref}</div>}
                        <div className="truncate" title={sub.notes}>{sub.notes || "—"}</div>
                        {sub.status === "rejected" && sub.reject_reason && (
                          <div className="text-xs text-red-600 mt-0.5">Reason: {sub.reject_reason}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          sub.status === "pending" ? "bg-amber-50 text-amber-700" :
                          sub.status === "approved" ? "bg-green-50 text-green-700" :
                          "bg-red-50 text-red-700"
                        }`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid={`field-view-${sub.id}`}
                            onClick={() => openFieldReceipt(sub.id)}
                            className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500"
                            title="View receipt"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!readOnly && sub.status === "pending" && (
                            <>
                              <button
                                data-testid={`field-approve-${sub.id}`}
                                onClick={() => approveFieldSub(sub)}
                                className="px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700 hover:bg-green-100"
                              >
                                Approve
                              </button>
                              <button
                                data-testid={`field-reject-${sub.id}`}
                                onClick={() => { setRejectingSub(sub); setRejectReason(""); }}
                                className="px-2 py-1 text-xs font-medium rounded bg-red-50 text-red-700 hover:bg-red-100"
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Field Submission Receipt Preview */}
      {fieldPreviewUrl && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={closeFieldPreview} data-testid="field-receipt-preview">
          <div className="relative max-w-3xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeFieldPreview} className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
              <X className="w-4 h-4" />
            </button>
            <img src={fieldPreviewUrl} alt="Receipt" className="w-full h-full max-h-[90vh] object-contain rounded-lg bg-white" />
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectingSub && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="field-reject-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-stone-800 mb-1">Reject submission</h3>
            <p className="text-sm text-stone-500 mb-4">
              From <span className="font-medium text-stone-700">{rejectingSub.user_name}</span> — {formatUSD(rejectingSub.amount)}
            </p>
            <label className="block text-sm font-medium text-stone-700 mb-1">Reason (optional)</label>
            <textarea
              data-testid="field-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
              placeholder="Tell the employee why this was rejected…"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setRejectingSub(null); setRejectReason(""); }} className="px-4 py-2 text-sm rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
              <button data-testid="field-reject-confirm" onClick={submitReject} className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium">Reject</button>
            </div>
          </div>
        </div>
      )}
      {receiptModal && receiptUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="receipt-modal" onClick={() => { setReceiptModal(null); URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); setReceiptExpense(null); }}>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
              <h3 className="font-semibold text-stone-800 flex items-center gap-2 text-sm">
                <Receipt className="w-4 h-4 text-forest-600" /> Receipt Preview
              </h3>
              <div className="flex items-center gap-2">
                <a href={receiptUrl} download className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100 transition-colors" data-testid="download-receipt-btn">
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button onClick={() => { setReceiptModal(null); URL.revokeObjectURL(receiptUrl); setReceiptUrl(null); setReceiptExpense(null); }} className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500 transition-colors" data-testid="close-receipt-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="relative p-4 max-h-[75vh] overflow-auto flex items-center justify-center bg-stone-100">
              <img src={receiptUrl} alt="Receipt" className="max-w-full max-h-[65vh] rounded-lg shadow-md object-contain" />
              {/* Status Badge Watermark */}
              {receiptExpense && (
                <div className="absolute top-6 right-6 flex flex-col gap-1.5" data-testid="receipt-badges">
                  <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider shadow-lg ${
                    receiptExpense.status === "paid" ? "bg-green-600 text-white" :
                    receiptExpense.status === "scheduled" ? "bg-blue-600 text-white" :
                    "bg-amber-500 text-white"
                  }`}>
                    {receiptExpense.doc_status === "paid" ? "PAID" : receiptExpense.status === "scheduled" ? "SCHEDULED" : "PENDING"}
                  </span>
                  {receiptExpense.reimbursement_status === "awaiting" && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider shadow-lg bg-orange-500 text-white">AWAITING REIMB.</span>
                  )}
                  {receiptExpense.reimbursement_status === "reimbursed" && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider shadow-lg bg-cyan-600 text-white">REIMBURSED</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Centered Confirm Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmModal.danger ? "bg-red-50" : "bg-forest-50"}`}>
              {confirmModal.danger
                ? <AlertTriangle className="w-7 h-7 text-red-500" />
                : <Info className="w-7 h-7 text-forest-600" />
              }
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-2">{confirmModal.title}</h3>
            <p className="text-sm text-stone-500 mb-6">{confirmModal.message}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setConfirmModal(null)} data-testid="confirm-cancel-btn" className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
              <button onClick={confirmModal.onConfirm} data-testid="confirm-ok-btn" className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors ${confirmModal.danger ? "bg-red-600 hover:bg-red-700" : "bg-forest-600 hover:bg-forest-700"}`}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick "Mark as Paid" Modal */}
      {quickPaidInv && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4"
          onClick={closeQuickPaidModal}
          data-testid="quick-paid-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" /> Mark Invoice as Paid
                </h3>
                <p className="text-xs text-stone-500 mt-1">
                  {quickPaidInv.invoice_number} · {quickPaidInv.client_name} · {formatUSD(quickPaidInv.total_amount || 0)}
                </p>
              </div>
              <button onClick={closeQuickPaidModal} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3 mt-4">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Payment Date</label>
                <input
                  type="date"
                  value={quickPaidDate}
                  onChange={(e) => setQuickPaidDate(e.target.value)}
                  data-testid="quick-paid-date-input"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Observation <span className="text-stone-400">(optional)</span></label>
                <input
                  value={quickPaidNote}
                  onChange={(e) => setQuickPaidNote(e.target.value)}
                  placeholder="e.g., Paid by check #2045 · Zelle · Cash on site"
                  data-testid="quick-paid-note-input"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                />
                <p className="text-[11px] text-stone-400 mt-1">Appended to the invoice notes for auditing.</p>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={closeQuickPaidModal}
                disabled={quickPaidBusy}
                className="flex-1 px-4 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmQuickPaid}
                disabled={quickPaidBusy || !quickPaidDate}
                data-testid="quick-paid-confirm"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                {quickPaidBusy ? "Saving…" : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month drill-down modal */}
      <MonthDetailModal
        open={!!detailMonth}
        monthKey={detailMonth?.key}
        year={detailMonth?.year}
        month={detailMonth?.month}
        label={detailMonth?.label}
        onClose={() => setDetailMonth(null)}
        api={api}
        refreshSignal={detailRefreshSignal}
        onOpenServiceOrder={onOpenServiceOrder ? (soId) => { setDetailMonth(null); onOpenServiceOrder(soId); } : null}
      />
    </div>
  );
}
